import React, { useState, useEffect } from "react";
import PageHeader from "../components/shared/PageHeader";
import PageContainer from "../components/shared/PageContainer";
import {
  Alert,
  Button,
  Drawer,
  Select,
  Spinner,
  TextInput,
  Td,
  Th,
  Tr,
} from "../components/ui";
import { entityApi } from "../services/api";

interface Supplier {
  id: number;
  businessName: string;
  businessDisplayName?: string;
  businessAddress?: string;
  businessPostcode?: string;
  templateSelection?: string;
  createdAt?: string;
}

interface TemplateRow {
  id: number;
  name: string;
}

const AVAILABLE_TEMPLATES = [
  "Celebration Cake",
  "Wedding Cake",
  "Daily Cake Order",
  "Standard",
];

const DEFAULT_TEMPLATE_ROW: TemplateRow = { id: 1, name: "" };

const CreateSupplier: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [viewingSupplier, setViewingSupplier] = useState<Supplier | null>(null);

  // Form State
  const [formBusinessName, setFormBusinessName] = useState("");
  const [formDisplayName, setFormDisplayName] = useState("");
  const [formAddress, setFormAddress] = useState("");
  const [formPostcode, setFormPostcode] = useState("");
  const [templates, setTemplates] = useState<TemplateRow[]>([{ ...DEFAULT_TEMPLATE_ROW }]);

  const fetchSuppliers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await entityApi.getAllSuppliers();
      setSuppliers(data);
    } catch (err: any) {
      setError("Failed to fetch suppliers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const resetForm = () => {
    setEditingSupplier(null);
    setFormBusinessName("");
    setFormDisplayName("");
    setFormAddress("");
    setFormPostcode("");
    setTemplates([{ ...DEFAULT_TEMPLATE_ROW }]);
  };

  const handleOpenAdd = () => {
    resetForm();
    setShowOffcanvas(true);
  };

  const handleOpenEdit = (supplier: Supplier) => {
    resetForm();
    setEditingSupplier(supplier);
    setFormBusinessName(supplier.businessName);
    setFormDisplayName(supplier.businessDisplayName || "");
    setFormAddress(supplier.businessAddress || "");
    setFormPostcode(supplier.businessPostcode || "");
    // Parse stored templates back into rows
    const storedTemplates = supplier.templateSelection;
    if (storedTemplates) {
      const names = storedTemplates.split(",").map((s) => s.trim()).filter(Boolean);
      setTemplates(names.map((name, i) => ({ id: Date.now() + i, name })));
    } else {
      setTemplates([{ ...DEFAULT_TEMPLATE_ROW }]);
    }
    setShowOffcanvas(true);
  };

  const handleOpenView = (supplier: Supplier) => {
    setViewingSupplier(supplier);
    setShowViewOffcanvas(true);
  };

  const handleClose = () => {
    setShowOffcanvas(false);
  };

  const handleCloseView = () => {
    setShowViewOffcanvas(false);
    setViewingSupplier(null);
  };

  const handleTemplateChange = (id: number, value: string) =>
    setTemplates((prev) =>
      prev.map((t) => (t.id === id ? { ...t, name: value } : t))
    );
  const handleAddTemplate = () =>
    setTemplates((prev) => [...prev, { id: Date.now(), name: "" }]);
  const handleRemoveTemplate = (id: number) =>
    setTemplates((prev) => prev.filter((t) => t.id !== id));

  const handleDelete = async (supplier: Supplier) => {
    if (!window.confirm(`Are you sure you want to delete "${supplier.businessName}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await entityApi.deleteSupplier({ id: supplier.id });
      fetchSuppliers();
    } catch (err) {
      alert("Failed to delete supplier.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formBusinessName) {
      alert("Please fill in all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        id: editingSupplier?.id || 0,
        businessName: formBusinessName,
        businessDisplayName: formDisplayName,
        businessAddress: formAddress,
        businessPostcode: formPostcode,
        templateSelection: templates
          .filter((t) => t.name.trim())
          .map((t) => t.name)
          .join(", "),
      };

      if (editingSupplier) {
        await entityApi.updateSupplier(payload);
      } else {
        await entityApi.createSupplier(payload);
      }

      setShowOffcanvas(false);
      fetchSuppliers();
    } catch (err) {
      alert("Failed to save supplier.");
    } finally {
      setSubmitting(false);
    }
  };

  const formId = "supplier-form";

  return (
    <PageContainer>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <PageHeader title="Manage Suppliers" subTitle="Suppliers" />
        <Button variant="primary" onClick={handleOpenAdd} className="shrink-0">
          <i className="bi bi-truck" />
          Create Supplier
        </Button>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      <section className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <Th>Business Name</Th>
                <Th>Display Name</Th>
                <Th>Business Address</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center">
                    <Spinner className="mx-auto" />
                  </td>
                </tr>
              ) : suppliers.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-12 text-center text-sm text-zinc-500"
                  >
                    No suppliers found.
                  </td>
                </tr>
              ) : (
                suppliers.map((supplier) => (
                  <Tr key={supplier.id}>
                    <Td className="font-medium text-zinc-900">
                      {supplier.businessName}
                    </Td>
                    <Td className="text-zinc-500">
                      {supplier.businessDisplayName || "—"}
                    </Td>
                    <Td className="text-zinc-500">
                      {supplier.businessAddress || "—"}
                    </Td>
                    <Td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          variant="ghost"
                          onClick={() => handleOpenView(supplier)}
                          className="px-3 py-1.5 text-xs"
                        >
                          <i className="bi bi-eye" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleOpenEdit(supplier)}
                          className="px-3 py-1.5 text-xs"
                        >
                          <i className="bi bi-pencil" />
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => handleDelete(supplier)}
                          className="px-3 py-1.5 text-xs"
                        >
                          <i className="bi bi-trash" />
                          Delete
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Side panel for Create/Edit Supplier */}
      <Drawer
        open={showOffcanvas}
        onOpenChange={(open) => !open && handleClose()}
        title={editingSupplier ? "Edit Supplier" : "Create New Supplier"}
        footer={
          <>
            <Button variant="outline" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              form={formId}
              variant="primary"
              disabled={submitting}
              className="flex-1"
            >
              {submitting ? (
                <>
                  <Spinner size="size-4" />
                  Saving…
                </>
              ) : editingSupplier ? (
                "Update Supplier"
              ) : (
                "Create Supplier"
              )}
            </Button>
          </>
        }
      >
        <form id={formId} onSubmit={handleSubmit} className="space-y-4">
          <TextInput
            label="Business Name *"
            type="text"
            placeholder="e.g. ABC Supplies Ltd"
            value={formBusinessName}
            onChange={(e) => setFormBusinessName(e.target.value)}
            required
          />

          <TextInput
            label="Business Display Name"
            type="text"
            placeholder="e.g. ABC Supplies"
            value={formDisplayName}
            onChange={(e) => setFormDisplayName(e.target.value)}
          />

          <TextInput
            label="Business Address"
            type="text"
            placeholder="e.g. 123 High Street, London"
            value={formAddress}
            onChange={(e) => setFormAddress(e.target.value)}
          />

          <TextInput
            label="Business Postcode"
            type="text"
            placeholder="e.g. SW1A 1AA"
            value={formPostcode}
            onChange={(e) => setFormPostcode(e.target.value)}
          />

          <div className="space-y-2">
            <span className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
              Templates
            </span>
            {templates.map((template, index) => (
              <div key={template.id} className="flex items-center gap-2">
                <Select
                  value={template.name}
                  onChange={(e) =>
                    handleTemplateChange(template.id, e.target.value)
                  }
                  className="flex-1"
                >
                  <option value="">Select Template</option>
                  {AVAILABLE_TEMPLATES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
                {templates.length > 1 && (
                  <Button
                    variant="danger"
                    aria-label="Remove template"
                    onClick={() => handleRemoveTemplate(template.id)}
                    className="shrink-0 px-3 py-2"
                  >
                    <i className="bi bi-dash" />
                  </Button>
                )}
                {index === templates.length - 1 && (
                  <Button
                    variant="outline"
                    aria-label="Add template"
                    onClick={handleAddTemplate}
                    className="shrink-0 px-3 py-2"
                  >
                    <i className="bi bi-plus" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </form>
      </Drawer>

      {/* Side panel for Supplier details */}
      <Drawer
        open={showViewOffcanvas}
        onOpenChange={(open) => !open && handleCloseView()}
        title="Supplier Details"
        footer={
          <>
            <Button variant="outline" onClick={handleCloseView} className="flex-1">
              Close
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => {
                const supplier = viewingSupplier;
                handleCloseView();
                if (supplier) handleOpenEdit(supplier);
              }}
            >
              <i className="bi bi-pencil" />
              Edit
            </Button>
          </>
        }
      >
        {viewingSupplier && (
          <dl className="space-y-5">
            {[
              { label: "Business Name", value: viewingSupplier.businessName },
              {
                label: "Display Name",
                value: viewingSupplier.businessDisplayName || "—",
              },
              {
                label: "Business Address",
                value: viewingSupplier.businessAddress || "—",
              },
              {
                label: "Business Postcode",
                value: viewingSupplier.businessPostcode || "—",
              },
              {
                label: "Templates",
                value: viewingSupplier.templateSelection || "—",
              },
              ...(viewingSupplier.createdAt
                ? [
                    {
                      label: "Created At",
                      value: new Date(
                        viewingSupplier.createdAt
                      ).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      }),
                    },
                  ]
                : []),
            ].map(({ label, value }) => (
              <div key={label}>
                <dt className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
                  {label}
                </dt>
                <dd className="mt-1 text-sm text-zinc-900">{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </Drawer>
    </PageContainer>
  );
};

export default CreateSupplier;
